import {
    api_eateries_impressions_del,
    api_eateries_impressions_post,
    Cabbage,
    DishInfo,
    DishKind,
    EateryAudience,
    EateryDecor,
    EateryDomain,
    EateryGroup,
    EateryImpression,
    EateryInfo,
    EateryReviewInfo,
    Gene,
    UserImpression,
} from 'api'
import { cloneDefault } from 'shared'
import { createStore, produce } from 'solid-js/store'
import { ssr_data } from './server'
import { EATERY_INFO_DEFAULT } from 'shared/defs'
import { USER_RATE_EATERY } from './dbLabels'

export const ARIA_TEXTS: string[] = [
    'به این زمین گرد که به‌سرعت می گردد بنگر که درمان ما در آن است و درد ما نیز، نگاه کن و ببین که نه گردش زمانه آن را می فرساید و نه رنج و بهبودی حال بشر آن را به آتش می کشد، نه آرام می شود و نه همچون ما تباهی می پذیرد',
    'اولین مرحله شناخت آفرینش همانا خرد است چشم و گوش و زبان سه نگهبان اویند که لاجرم هر چه نیکی و شر است از همین سه ریشه می گیرد. و افسوس که بدنبال کنندگان خرد اندکند باید که به سخن دانندگان راه جست و باید جهان را کاوش نمود و از هر کسی دانشی آموخت و یک دم را هم برای آموختن نباید از دست داد.',
]

export type EaterySection = 'info' | 'menu' | 'reviews'

export type ActiveCat = DishKind | 'best_dishes'
export type EateryState = {
    eatery: EateryInfo
    menu: DishInfo[]
    menu_loading: boolean
    menu_done: boolean
    // active cat
    menu_ac: ActiveCat
    // active dish
    menu_ad: DishInfo | null
    menu_search: string
    menu_cart: {
        items: Record<NonNullable<Gene>, number>
        updated: number
    }
    cabbages: Cabbage[]
    impressions: EateryImpression[]
    reviews: EateryReviewInfo[]
    loading: boolean
    reviews_loading: boolean
    not_for_guest: boolean
    data_update: number
    transform: number
}
export const ESD: EateryState = {
    loading: false,
    eatery: cloneDefault(EATERY_INFO_DEFAULT),
    menu: [],
    menu_ac: 'best_dishes',
    menu_ad: null,
    menu_loading: false,
    menu_done: true,
    menu_cart: {
        items: {},
        updated: 0,
    },
    menu_search: '',
    impressions: [],
    cabbages: [],
    reviews: [],
    reviews_loading: false,
    not_for_guest: false,
    data_update: 0,
    transform: 0,
}
const [eatery_state, setEateryState] = createStore<EateryState>(
    ssr_data('map-eatery-p', ESD)
)

export function eatery_client_transform() {
    setEateryState('transform', 100)
}

export function eatery_state_clear() {
    setEateryState(
        produce(s => {
            s.eatery.gene = ''
            s.menu_done = false
            s.loading = true
            s.menu_loading = true
            s.menu_search = ''
            s.reviews_loading = true
            s.not_for_guest = false
            s.menu_ac = 'best_dishes'
            s.menu = []
            s.cabbages = []
            s.reviews = []
            s.transform = 100
            s.menu_cart = {
                updated: Date.now(),
                items: {},
            }
        })
    )
}
export function eatery_state_menu_active_cat(ac: ActiveCat) {
    setEateryState({ menu_ac: ac })
}
export const eatery_state_menu_active_dish = (dish: EateryState['menu_ad']) => {
    setEateryState({ menu_ad: dish })
}

export function eatery_search_menu(val: string) {
    setEateryState({ menu_search: val })
}
export function eatery_search_menu_clear() {
    setEateryState({ menu_search: '' })
}
export function eatery_impressions_clear() {
    setEateryState({ impressions: [] })
}

export function eatery_get_cart_count(gene: Gene): number {
    if (!gene) return 0
    return eatery_state.menu_cart.items[gene] ?? 0
}
export function eatery_add_to_cart(d: DishInfo) {
    setEateryState(
        produce(s => {
            if (!d.gene) return

            const current = s.menu_cart.items[d.gene] ?? 0

            s.menu_cart.items[d.gene] = Math.min(current + 1, 100)
            s.menu_cart.updated = Date.now()
        })
    )
}
export function eatery_remove_from_cart(gene: Gene, all: boolean = false) {
    setEateryState(
        produce(s => {
            if (!gene) return

            const current = s.menu_cart.items[gene] ?? 0

            if (current <= 1 || all) {
                delete s.menu_cart.items[gene]
            } else {
                s.menu_cart.items[gene] = current - 1
            }

            s.menu_cart.updated = Date.now()
        })
    )
}

export async function eatery_impression_add(
    imp: UserImpression,
    emojiMinus?: number | null
) {
    setEateryState(
        produce(s => {
            let id = s.impressions.findIndex(
                i => i.impression == imp.impression
            )

            if (id == -1) {
                // hasnt emoji
                s.impressions.push({
                    count: 1,
                    impression: imp.impression,
                })
            } else {
                // has emoji
                s.impressions[id]!.count += 1
            }

            if (emojiMinus) {
                let eid = s.impressions.findIndex(
                    i => i.impression == emojiMinus
                )
                if (eid != -1) {
                    let newCount = s.impressions[eid]!.count - 1

                    if (newCount > 0) {
                        s.impressions[eid]!.count = newCount
                    } else {
                        s.impressions.splice(eid, 1)
                    }
                }
            }
        })
    )

    const gene = imp.eatery

    if (!gene) return

    api_eateries_impressions_post({ gene, imp: imp.impression })
}
export async function eatery_impression_del(imp: number, eateryGene: Gene) {
    if (!eateryGene) return

    setEateryState(
        produce(s => {
            let id = s.impressions.findIndex(i => i.impression == imp)

            if (id == -1) return

            if (s.impressions[id]!.count - 1 > 0) {
                s.impressions[id]!.count = s.impressions[id]!.count - 1
            } else {
                s.impressions.splice(id, 1)
            }
        })
    )

    api_eateries_impressions_del({ gene: eateryGene })
}
export function pop_reaction_confetties(
    img: string,
    coords?: { x?: number; y?: number }
) {
    // @ts-ignore
    if (typeof window === 'undefined' || typeof window.confetti !== 'function')
        return

    const clamp = (v?: number) =>
        typeof v === 'number' && isFinite(v)
            ? Math.max(0, Math.min(1, v))
            : undefined

    const cx = clamp(coords?.x)
    const cy = clamp(coords?.y)

    let origin = {}

    if (cx !== undefined && cy !== undefined) {
        origin = {
            x: cx,
            y: cy,
        }
    }

    // @ts-ignore
    window.confetti({
        origin,
        spread: 360,
        ticks: 200,
        gravity: 0.5,
        decay: 1,
        startVelocity: 5,
        particleCount: 70,
        scalar: 2,
        shapes: ['image'],
        shapeOptions: {
            image: [
                { src: img, width: 32, height: 32 },
                { src: img, width: 32, height: 32 },
                { src: img, width: 32, height: 32 },
                { src: img, width: 32, height: 32 },
                { src: img, width: 32, height: 32 },
                { src: img, width: 32, height: 32 },
                { src: img, width: 32, height: 32 },
                { src: img, width: 32, height: 32 },
            ],
        },
    })
}

export async function eatery_impressions_set(imps: EateryImpression[]) {
    setEateryState('impressions', imps)
}

export function eatery_state_add_review(v: EateryReviewInfo) {
    setEateryState(
        produce(s => {
            s.reviews.unshift(v)
        })
    )
}

type QuickFileT = {
    file: File
    url: string
}
export type QuickT = {
    show: boolean
    loading: {
        state: boolean
        status: [number, number]
    }
    status: 'error' | 'success' | null
    type:
        | 'photo'
        | 'menu'
        | 'phone'
        | 'tables'
        | 'decor'
        | 'audience'
        | 'cat'
        | 'tables'
        | null
    value: {
        files: QuickFileT[] | null
        txt: string
        mains: {
            decor: EateryDecor
            audience: EateryAudience
            group: EateryGroup
            domain: EateryDomain
        } | null
    }
}

export const QUICK_DEF: QuickT = {
    show: false,
    type: null,
    loading: {
        state: false,
        status: [-1, -1],
    },
    status: null,
    value: {
        files: null,
        txt: '',
        mains: null,
    },
}

type EateryExtra =
    | 'oh'
    | 'po'
    | 'nav'
    | 'anonymous'
    | 'review'
    | 'ownership'
    | 'war-warning'
    | 'buy-discount'
    | 'invite-for-rob'
    | 'tags'
    | 'menu-cart'
    | 'active-dish'
    | null

type EateryMapModel = {
    section: EaterySection
    scrolling: boolean

    quick: QuickT

    quickSearch: 'audience' | 'decor' | 'category' | null

    imgFullscreen: boolean
    extras: EateryExtra

    emojies: boolean
    reactions: boolean
}

const EATERY_MAP_DEF: EateryMapModel = {
    section: 'info',
    scrolling: false,

    imgFullscreen: false,
    extras: null,

    quickSearch: null,

    quick: cloneDefault(QUICK_DEF),

    emojies: false,
    reactions: false,
}
const [eateryMap, setEateryMap] = createStore<EateryMapModel>(
    cloneDefault(EATERY_MAP_DEF)
)

export const eateryExtrasToggle = (show: EateryMapModel['extras']) => {
    setEateryMap('extras', show)
}
export const eateryFullscreenImgsToggle = (
    show: EateryMapModel['imgFullscreen']
) => {
    setEateryMap('imgFullscreen', show)
}
export const eatery_map_clear = () => {
    setEateryMap(cloneDefault(EATERY_MAP_DEF))
}

export const rateEateryIn3Hours = (gene: Gene) => {
    if (!gene) return

    const now = Date.now()

    localStorage.setItem(
        USER_RATE_EATERY,
        JSON.stringify({
            gene,
            now,
        })
    )
}

export {
    type EateryMapModel,
    eateryMap,
    setEateryMap,
    eatery_state,
    setEateryState,
}
